/** Soft-validated secret field types (hint only — never blocks Save). */
export type SoftValidatedFieldType = 'email' | 'url' | 'phone' | 'number';

const SOFT_TYPES: readonly SoftValidatedFieldType[] = ['email', 'url', 'phone', 'number'];

export function isSoftValidatedType(type: string): type is SoftValidatedFieldType {
  return (SOFT_TYPES as readonly string[]).includes(type);
}

/** Basic email: local@domain.tld */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** http(s)://… or bare domain-ish (example.com, www.ex.co.uk/path). */
const URL_WITH_SCHEME_RE = /^https?:\/\/.+/i;
const URL_DOMAINISH_RE = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?:[/:?#].*)?$/i;

/** Digits, spaces, +, -, () only. */
const PHONE_CHARS_RE = /^[0-9+\-() ]+$/;

/** Finite decimal (optional leading minus). */
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

export function isValidSecretEmail(value: string): boolean {
  return EMAIL_RE.test(value);
}

export function isValidSecretUrl(value: string): boolean {
  return URL_WITH_SCHEME_RE.test(value) || URL_DOMAINISH_RE.test(value);
}

export function isValidSecretPhone(value: string): boolean {
  if (!PHONE_CHARS_RE.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 6;
}

export function isValidSecretNumber(value: string): boolean {
  if (!NUMBER_RE.test(value)) return false;
  return Number.isFinite(Number(value));
}

function isValidForType(type: SoftValidatedFieldType, value: string): boolean {
  switch (type) {
    case 'email':
      return isValidSecretEmail(value);
    case 'url':
      return isValidSecretUrl(value);
    case 'phone':
      return isValidSecretPhone(value);
    case 'number':
      return isValidSecretNumber(value);
  }
}

/**
 * Soft warning key for non-empty invalid values.
 * Empty / whitespace-only / unknown type → null (no warning).
 */
export function getSecretFieldSoftWarning(
  type: string,
  value: string,
): SoftValidatedFieldType | null {
  if (!isSoftValidatedType(type)) return null;
  if (!value.trim()) return null;
  return isValidForType(type, value.trim()) ? null : type;
}
